import React from 'react';
import { useLocation } from 'react-router-dom';
import { injectIntl, IntlShape } from '@edx/frontend-platform/i18n';
import { logError } from '@edx/frontend-platform/logging';

import messages from './messages';

import './index.scss';

interface ErrorBoundaryPanelProps {
  intl: IntlShape;
  locationKey: string;
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}
class ErrorBoundaryPanel extends React.Component<ErrorBoundaryPanelProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryPanelProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidUpdate(prevProps: ErrorBoundaryPanelProps): void {
    const { locationKey } = this.props;
    const { hasError } = this.state;
    if (hasError && prevProps.locationKey !== locationKey) {
      this.setState({ hasError: false });
    }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    logError(error, { componentStack: info.componentStack });
  }

  private handleRetry = (): void => {
    window.location.reload();
  };

  render(): React.ReactNode {
    const { hasError } = this.state;
    const { children, intl } = this.props;

    if (!hasError) {
      return children;
    }

    return (
      <div className="error-boundary-panel">
        <h1 className="leg-title">{intl.formatMessage(messages.title)}</h1>
        <p className="leg-p">{intl.formatMessage(messages.message)}</p>
        <div className="error-boundary-actions">
          <button type="button" className="leg-primary-button" onClick={this.handleRetry}>
            {intl.formatMessage(messages.retry)}
          </button>
          <a href="/" className="leg-primary-button error-boundary-secondary">
            {intl.formatMessage(messages.backHome)}
          </a>
        </div>
      </div>
    );
  }
}

const IntlErrorBoundaryPanel = injectIntl(ErrorBoundaryPanel);

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({ children }) => {
  const { key } = useLocation();
  return <IntlErrorBoundaryPanel locationKey={key}>{children}</IntlErrorBoundaryPanel>;
};

export default ErrorBoundary;
